import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

import { TeamAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { useTeam } from "~/contexts/TeamProvider";

export type OnboardingStepField =
  | "onboarding_tour_watched_at"
  | "onboarding_launch_call_at";

/**
 * Marks a step of the team's launch checklist as done, once. Only the team
 * admin may write it, which is the only member of an Autopto team until it
 * pays. The checklist is a nudge: a failed write isn't worth bothering the
 * user.
 */
export function useMarkOnboardingStep() {
  const { team, refetchTeam } = useTeam();

  const { mutate } = useMutation({
    mutationFn: ({
      teamId,
      field,
    }: {
      teamId: number;
      field: OnboardingStepField;
    }) => {
      const attributes: TeamAttributes = {};
      attributes[field] = new Date().toISOString();

      return apiClient.teams.teamsPartialUpdate(teamId, attributes);
    },
    onSuccess: () => void refetchTeam(),
  });

  return useCallback(
    (field: OnboardingStepField) => {
      if (!team || team[field]) return;

      mutate({ teamId: team.id, field });
    },
    [team, mutate],
  );
}
